import { useCallback } from 'react';
import { Link } from 'react-router-dom';
import { usePoll, useApi } from '../lib/api.ts';
import { Empty, ErrorState, Loading, Status, orgPath, useOrg } from '../lib/ui.tsx';

interface OverviewData {
  screens: { id: string; name: string; locationName: string; connectivity: string; desiredSequence: number; activeSequence: number | null; currentProgramName: string | null; playerState: string | null }[];
  counts: { screens: number; connected: number; stale: number; offline: number; unpaired: number; behind: number };
  failingPublications: { id: string; name: string; status: string; failed: number }[];
  openAlerts: { kind: string; severity: string; n: number }[];
  nextSteps: { id: string; label: string; href: string }[];
}

export function Overview() {
  const org = useOrg();
  const { data, error, loading, reload } = useApi<OverviewData>(orgPath(org, '/overview'), [org.id]);
  usePoll(useCallback(() => void reload(), [reload]), 15_000);
  return (
    <>
      <div className="page-head">
        <div>
          <h1>Overview</h1>
          <p>What is playing now, what needs attention, and what to do next.</p>
        </div>
      </div>
      {error && <ErrorState error={error} retry={reload} />}
      {loading && !data && <Loading rows={4} />}
      {data && (
        <div className="stack" style={{ gap: 24 }}>
          {!!data.nextSteps.length && (
            <div className="card card-pad">
              <h2 style={{ marginBottom: 12 }}>Get started</h2>
              <div className="row">
                {data.nextSteps.map((s, i) => (
                  <Link key={s.id} to={s.href} className={`btn${i === 0 ? ' btn-primary' : ''}`}>
                    {i + 1}. {s.label}
                  </Link>
                ))}
              </div>
            </div>
          )}
          <div className="grid grid-stats">
            {[
              ['Screens', data.counts.screens, null],
              ['Connected', data.counts.connected, 'ok'],
              ['Stale heartbeat', data.counts.stale, 'warn'],
              ['Offline', data.counts.offline, 'danger'],
              ['Not paired', data.counts.unpaired, null],
              ['Behind latest release', data.counts.behind, 'warn'],
            ].map(([label, value, tone]) => (
              <div key={label as string} className="card stat">
                <div className="value" style={{ color: tone && (value as number) > 0 ? `var(--${tone})` : undefined }}>{value as number}</div>
                <div className="label">{label as string}</div>
              </div>
            ))}
          </div>
          {!!data.failingPublications.length && (
            <div className="card">
              <div className="card-head"><h2>Deployments needing attention</h2></div>
              <table className="table">
                <tbody>
                  {data.failingPublications.map((p) => (
                    <tr key={p.id}>
                      <td><Link to={`/publications/${p.id}`}>{p.name}</Link></td>
                      <td><Status value={p.status} /></td>
                      <td className="muted">{p.failed} screen(s) failed</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="card">
            <div className="card-head">
              <h2>Now playing</h2>
              <span className="subtle">Reported by players · not a live screenshot</span>
            </div>
            {data.screens.length === 0 ? (
              <Empty title="No screens yet" action={<Link className="btn btn-primary" to="/screens?pair=1">Pair a screen</Link>}>
                Install LumaCast Player on a TV, stick or tablet and pair it with a one-time code.
              </Empty>
            ) : (
              <div className="table-wrap">
                <table className="table">
                  <thead><tr><th>Screen</th><th>Location</th><th>Connection</th><th>Playing</th><th>Release</th></tr></thead>
                  <tbody>
                    {data.screens.map((s) => (
                      <tr key={s.id}>
                        <td><Link to={`/screens?screen=${s.id}`}>{s.name}</Link></td>
                        <td className="muted">{s.locationName}</td>
                        <td><Status value={s.connectivity === 'unknown' ? 'unpaired' : s.connectivity} /></td>
                        <td>{s.currentProgramName ?? <span className="subtle">{s.playerState ?? '—'}</span>}</td>
                        <td className="mono">
                          {s.activeSequence ?? '—'} / {s.desiredSequence || '—'}
                          {s.activeSequence !== null && s.activeSequence < s.desiredSequence && <span className="badge warn" style={{ marginLeft: 8 }}>updating</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          {!!data.openAlerts.length && (
            <div className="card card-pad row">
              <strong>Open alerts:</strong>
              {data.openAlerts.map((a) => <Status key={a.kind} value={a.severity} label={`${a.kind.replace(/_/g, ' ')} · ${a.n}`} />)}
              <Link to="/alerts" className="btn btn-sm">Review</Link>
            </div>
          )}
        </div>
      )}
    </>
  );
}
